import {ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, Output} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {GeneralOption} from '@core/interfaces/system/system-common';

@Component({
    selector: 'ngx-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectComponent),
            multi: true,
        },
    ],
})
export class SelectComponent implements ControlValueAccessor {
    @Input() options: GeneralOption[] = [];
    @Input() placeholder = 'Select';
    @Input() disabled = false;
    @Input() multiple = false;
    @Input() showSelectAll = false;
    @Input() showClearButton = true;

    /** Used in non-multi mode to optionally override label with custom logic */
    @Input() customLabel: (() => string) | null = null;

    @Output() valueChange = new EventEmitter<string | string[]>();

    value: string | string[] | null = null;

    selectAllOption: GeneralOption = {label: 'Select All', value: '__select_all__'};

    isAllSelected: boolean = false;

    onChange = (_: string | string[]) => {};
    onTouched = () => {};

    writeValue(val: string | string[] | null): void {
        if (this.multiple) {
            this.isAllSelected = val?.length === this.options?.length || false;
        }

        this.value = this.isAllSelected ? [...val, this.selectAllOption.value] : val;
    }

    registerOnChange(fn: (val: string | string[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onValueChange(val: string | string[]) {
        let _value = val;

        if (
            Array.isArray(_value) &&
            _value.length === this.options.length &&
            !_value.includes(this.selectAllOption.value)
        ) {
            _value.push(this.selectAllOption.value);

            this.isAllSelected = true;
        } else if (Array.isArray(_value) && _value.length !== this.options.length + 1) {
            _value = _value.filter((item) => item !== this.selectAllOption.value);

            this.isAllSelected = false;
        }

        this.value = _value;
        if (Array.isArray(_value)) {
            this.onChange(_value.filter((item) => item !== this.selectAllOption.value));
            this.valueChange.emit(_value.filter((item) => item !== this.selectAllOption.value));
        } else {
            this.onChange(_value);
            this.valueChange.emit(_value);
        }
    }

    toggleSelectAll() {
        if (Array.isArray(this.value) && this.value.length >= this.options.length) {
            this.onValueChange([]);

            this.isAllSelected = false;
        } else {
            const allValues = this.options.map((o) => o.value);
            allValues.push(this.selectAllOption.value);
            this.onValueChange(allValues);

            this.isAllSelected = true;
        }
    }

    get valuesAsArray() {
        return Array.isArray(this.value) ? this.value : [];
    }

    getMultiLabels(values: string[]) {
        let _labels = values?.reduce((acc, curr) => {
            if (acc !== '') acc += ', ';
            return (acc += this.options.find((option) => option.value === curr)?.label);
        }, '');
        return _labels || '';
    }

    getSingleLabel(): string {
        return this.options.find((option) => option.value === this.value)?.label;
    }

    clearAll() {
        this.writeValue(this.multiple ? [] : '');
        this.onValueChange(this.multiple ? [] : '');
        this.isAllSelected = false;
    }
}
